import {
  AlertCircleIcon,
  ExternalLinkIcon,
  PlusIcon,
  SlackIcon,
} from 'lucide-react';
import { useParams } from 'react-router-dom';
import invariant from 'tiny-invariant';

import { PermissionsGate } from '@/modules/auth/components/permissions/PermissionsGate';
import { permissions } from '@/modules/auth/permissions';
import { useConnectSlack } from '@/modules/integrations/hooks/useConnectSlack';
import { Alert, AlertDescription } from '@/modules/ui/components/alert/Alert';
import { Button } from '@/modules/ui/components/button/Button';
import Container from '@/modules/ui/components/container/Container';
import Loader from '@/modules/ui/components/loader/Loader';
import { PageHeader } from '@/modules/ui/components/page-header/PageHeader';
import UserExternalAccountList from '@/modules/user-external-accounts/components/UserExternalAccountList';
import { useCreateUserExternalAccountMutation } from '@/modules/user-external-accounts/mutations/useCreateUserExternalAccountMutation';
import { useUserExternalAccountsQuery } from '@/modules/user-external-accounts/queries/useUserExternalAccountsQuery';

const UserExternalAccounts = () => {
  const { id: userId } = useParams<{ id: string }>();
  // Slack OAuth runs in a new tab: refetch on return so the list shows the
  // connection without a reload.
  const externalAccountsQuery = useUserExternalAccountsQuery(userId, {
    refetchOnWindowFocus: 'always',
  });
  const createAccountMutation = useCreateUserExternalAccountMutation();
  const connectSlack = useConnectSlack();
  const isSlackConnected = externalAccountsQuery.data?.some(
    (account) => account.type === 'Slack' && account.status === 'Active',
  );

  const handleAddLinkedIn = async () => {
    invariant(userId, 'User ID is required');

    createAccountMutation.mutate(
      { userId, type: 'LinkedIn' },
      {
        onSuccess: (result) => {
          if (result?.signupLink) {
            window.location.href = result.signupLink as string;
          }
        },
      },
    );
  };

  return (
    <Container>
      <PageHeader
        title="External Accounts"
        action={
          <PermissionsGate
            permissions={[permissions.UserExternalAccounts.Write]}
          >
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                onClick={() => connectSlack('App', { newTab: true })}
              >
                <SlackIcon className="h-4 w-4" />
                Connect Slack
                <ExternalLinkIcon aria-hidden className="h-3.5 w-3.5" />
              </Button>
              <Button
                variant="primary"
                loading={createAccountMutation.isPending}
                onClick={handleAddLinkedIn}
              >
                <PlusIcon className="h-4 w-4" />
                Connect LinkedIn
              </Button>
            </div>
          </PermissionsGate>
        }
      />

      {externalAccountsQuery.data && !isSlackConnected && (
        <p className="text-muted-foreground mb-4 text-sm">
          Need admin approval? Once your Slack admin approves Finlay, click
          Connect Slack again.
        </p>
      )}

      {externalAccountsQuery.isError && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>Failed to load external accounts</AlertDescription>
        </Alert>
      )}

      {externalAccountsQuery.isLoading && <Loader />}

      {externalAccountsQuery.data && externalAccountsQuery.data?.length > 0 ? (
        <UserExternalAccountList
          externalAccounts={externalAccountsQuery.data}
          userId={userId!}
        />
      ) : (
        <Alert variant="default">
          <AlertCircleIcon />
          <AlertDescription>No external accounts connected.</AlertDescription>
        </Alert>
      )}
    </Container>
  );
};

export default UserExternalAccounts;
